import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { QuizSetData } from '@/api/gen/_model/quizSetData';
import type { SuccessBodyQuizSetDetailsRes } from '@/api/gen/_model/successBodyQuizSetDetailsRes';
import { NextQuizset } from '@/shared/components';
import { useIsInExternalPlatform } from '@/shared/externalPlatform';
import { cn } from '@/utils/tailwind.util';
import { hasFlashcardCards } from '../../../flashcard/utils';
import { useExportWorksheetHandler } from '../../cta-buttons/hooks/useExportWorksheetHandler';
import { useFlashcardHandler } from '../../cta-buttons/hooks/useFlashcardHandler';
import { LearningMaterialCard } from '../../learning-material-card/components/LearningMaterialCard';
import { MobileQuickActions } from '../../quick-actions/components';
import { QuizMetadataBar } from '../../quiz-metadata';
import { GClassRoomShareButton } from './GClassRoomShareButton';
import { LoginContentGuardDescription, LoginContentGuardGradient } from './LoginContentGuard';
import { PremiumMapBanner } from './PremiumMapBanner';
import { QuizItemCard } from './QuizItemCard';
interface QuizContentSectionProps {
  quizSetWithQuizList: SuccessBodyQuizSetDetailsRes;
  displayQuizList: QuizSetData[];
  shouldShowLoginGuard: boolean;
  onLinkToClassroom?: () => void;
}
export const QuizContentSection = ({
  quizSetWithQuizList,
  displayQuizList,
  shouldShowLoginGuard,
  onLinkToClassroom
}: QuizContentSectionProps) => {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const isInExternalPlatform = useIsInExternalPlatform();
  const handlePlanPurchaseClick = () => {
    void router.push('/pricing');
  };
  const handleExportWorksheet = useExportWorksheetHandler({
    quizData: quizSetWithQuizList.data
  });
  const handleFlashcard = useFlashcardHandler({
    quizId: quizSetWithQuizList.data.id,
    quizData: quizSetWithQuizList.data
  });
  const hasFlashcard = hasFlashcardCards(quizSetWithQuizList.data.quizList);
  return <div className={'flex flex-col gap-sm'} data-sentry-component="QuizContentSection" data-sentry-source-file="QuizContentSection.tsx">
      {quizSetWithQuizList.data?.isPremiumTemplate && <PremiumMapBanner onPlanPurchaseClick={handlePlanPurchaseClick} />}
      <QuizMetadataBar {...quizSetWithQuizList.data} data-sentry-element="QuizMetadataBar" data-sentry-source-file="QuizContentSection.tsx" />
      {!isInExternalPlatform && <MobileQuickActions className="tablet-sm:hidden" onFlashcard={hasFlashcard ? handleFlashcard : undefined} onExportWorksheet={handleExportWorksheet} onLinkToClassroom={onLinkToClassroom} />}
      {quizSetWithQuizList.data.materials.map((material, index) => <LearningMaterialCard key={index} learningMaterial={material} />)}
      <section className={cn('flex flex-col gap-md relative', shouldShowLoginGuard && 'max-h-[600px] mobile-lg:max-h-[800px] overflow-hidden')}>
        {shouldShowLoginGuard && <LoginContentGuardGradient />}
        {displayQuizList.map((d, index) => <QuizItemCard data={{
        ...d,
        id: d.id || ''
      }} index={index} key={index} />)}
      </section>

      {shouldShowLoginGuard && <LoginContentGuardDescription quizCount={quizSetWithQuizList.data.quizList.length} />}

      {quizSetWithQuizList.data.isNextQuizsetAvailable && <section className="flex flex-col gap-2xs">
          <h6 className="text-body-md-bold text-neutral">
            {t('common:nextQuiz.label')}
          </h6>
          <NextQuizset quizsetId={quizSetWithQuizList.data.nextQuizsetId} />
        </section>}

      {!shouldShowLoginGuard && !isInExternalPlatform && <div className={'flex w-full justify-end'}>
          <GClassRoomShareButton entryMapHashId={quizSetWithQuizList?.data?.entryMapHashId || ''} />
        </div>}
    </div>;
};